// Considered: building payloads inline inside `TrackCardMolecule`/`TrajectoryPanelOrganism` —
// rejected for the same reason as Stream's `buildStreamInspectorPayloads.ts`: one place per
// `InspectorPayload` kind so every entry point (band click, tick click, demand row) produces an
// identical payload for the same fact. Pure functions — unit-testable without mounting the panel.
//
// Source: design/metaspace-tools/metaspace-trajectory-v2.jsx `TJ` payload factory, translated
// into the real `InspectorPayload` shape from `types/inspector.d.ts`.

import type { TrajectoryBand, TrajectoryCadenceEntry, TrajectoryDemand, TrajectoryFreshnessSummary, TrajectorySeriesPoint, TrajectoryTrack } from 'types/trajectory';
import type {
  AspectInspectorPayload,
  ChordInspectorPayload,
  CoverageInspectorPayload,
  DemandInspectorPayload,
  FocusItemInspectorPayload,
  InspectorKeyValueRow,
  PeriodInspectorPayload,
  ScopeInspectorPayload,
  StalenessInspectorPayload,
} from 'types/inspector';

export type TrajectoryTranslateFn = (key: string, options?: Record<string, unknown>) => string;

function timelineAddress(sphereId: string, seq: string): string {
  return `/sphere/${sphereId}/timeline?seq=${seq}`;
}

/** A Health state-band interval — clicking a band both opens this AND scopes the Stream. */
export function buildScopePayload(
  track: TrajectoryTrack,
  band: TrajectoryBand,
  sphereId: string,
  t: TrajectoryTranslateFn
): ScopeInspectorPayload {
  const toLabel = band.to ? new Date(band.to).toLocaleDateString() : t('trajectory.scope.now', { defaultValue: 'now' });
  return {
    kind: 'scope',
    icon: 'lucide:crosshair',
    title: t('trajectory.scope.title', { label: track.label, state: band.state, defaultValue: `${track.label} ${band.state}` }),
    subtitle: t('trajectory.scope.subtitle', {
      defaultValue: 'Scopes the Stream to this interval × aspect.',
    }),
    address: `/sphere/${sphereId}/timeline?aspect=${track.aspectSystemName}&from=${band.from}`,
    rowsLabel: t('toolInspector.rowsLabel.interval', { defaultValue: 'INTERVAL' }),
    rows: [
      { label: t('toolInspector.rows.state', { defaultValue: 'State' }), value: band.state, mono: true },
      { label: t('toolInspector.rows.effective', { defaultValue: 'Effective' }), value: `${new Date(band.from).toLocaleDateString()} – ${toLabel}` },
    ],
    acts: [
      { id: 'scope_stream', label: t('trajectory.acts.scopeStream', { defaultValue: 'Scope Stream' }), variant: 'secondary', icon: 'lucide:filter' },
      { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
    ],
    context: { sphereId, aspectSystemName: track.aspectSystemName, seq: band.transitionSeq },
  };
}

/** The transition marker between two bands — the causing chord, never scopes the Stream. */
export function buildTransitionPayload(
  track: TrajectoryTrack,
  band: TrajectoryBand,
  sphereId: string,
  t: TrajectoryTranslateFn
): ChordInspectorPayload {
  const seq = band.transitionSeq as string;
  return {
    kind: 'chord',
    icon: 'lucide:git-commit',
    title: t('trajectory.transition.title', { label: track.label, state: band.state, defaultValue: `${track.label} turned ${band.state}` }),
    subtitle: t('trajectory.transition.subtitle', { defaultValue: "The causing chord." }),
    address: timelineAddress(sphereId, seq),
    rowsLabel: t('toolInspector.rowsLabel.cause', { defaultValue: 'CAUSE' }),
    rows: [
      { label: t('toolInspector.rows.new', { defaultValue: 'New' }), value: band.state, mono: true },
    ],
    acts: [
      { id: 'explain', label: t('toolInspector.acts.explain', { defaultValue: 'Explain' }), variant: 'secondary', icon: 'lucide:settings' },
      { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
    ],
    context: { sphereId, aspectSystemName: track.aspectSystemName, seq },
  };
}

/** A Gravity sparkline point — the observation behind it. */
export function buildSparkPointPayload(
  track: TrajectoryTrack,
  point: TrajectorySeriesPoint,
  sphereId: string,
  t: TrajectoryTranslateFn
): ChordInspectorPayload {
  return {
    kind: 'chord',
    icon: 'lucide:activity',
    title: t('trajectory.spark.title', { label: track.label, value: point.value, defaultValue: `${track.label} observed · ${point.value}` }),
    subtitle: t('trajectory.spark.subtitle', { defaultValue: 'The observation behind this point.' }),
    address: `/sphere/${sphereId}/aspect/${track.aspectSystemName}?t=${point.t}`,
    rowsLabel: t('toolInspector.rowsLabel.point', { defaultValue: 'POINT' }),
    rows: [
      { label: t('toolInspector.rows.value', { defaultValue: 'Value' }), value: String(point.value), mono: true },
      { label: t('toolInspector.rows.observedAt', { defaultValue: 'observedAt' }), value: point.t, mono: true },
    ],
    acts: [{ id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' }],
    context: { sphereId, aspectSystemName: track.aspectSystemName },
  };
}

/** A cadence "hit" tick — the check-in confirmation. */
export function buildCadenceHitPayload(
  track: TrajectoryTrack,
  entry: TrajectoryCadenceEntry,
  sphereId: string,
  t: TrajectoryTranslateFn
): ChordInspectorPayload {
  const rows: InspectorKeyValueRow[] = [
    { label: t('toolInspector.rows.statusGiven', { defaultValue: 'Status given' }), value: entry.statusValue ?? '', mono: true },
    { label: t('toolInspector.rows.by', { defaultValue: 'By' }), value: entry.checkedInBy?.name ?? '' },
  ];
  if (entry.checkedInAt) rows.push({ label: t('toolInspector.rows.submitted', { defaultValue: 'Submitted' }), value: entry.checkedInAt, mono: true });

  return {
    kind: 'chord',
    icon: 'lucide:check-circle',
    title: t('trajectory.cadence.hitTitle', { label: track.label, status: entry.statusValue, defaultValue: `${track.label} check-in · ${entry.statusValue}` }),
    address: `/sphere/${sphereId}/timeline`,
    rowsLabel: t('toolInspector.rowsLabel.checkIn', { defaultValue: 'CHECK-IN' }),
    rows,
    acts: [{ id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' }],
    context: { sphereId, aspectSystemName: track.aspectSystemName, periodId: entry.periodId, factorId: entry.factorId },
  };
}

/** A cadence "miss" tick — the repair loop, not an edit. */
export function buildCadenceMissPayload(
  track: TrajectoryTrack,
  entry: TrajectoryCadenceEntry,
  sphereId: string,
  t: TrajectoryTranslateFn
): PeriodInspectorPayload {
  return {
    kind: 'period',
    icon: 'lucide:calendar-x',
    title: t('trajectory.cadence.missTitle', { defaultValue: 'Missed check-in' }),
    subtitle: t('trajectory.cadence.missSubtitle', {
      defaultValue: 'The window closed with no attestation.',
    }),
    address: `/sphere/${sphereId}/cadence/${track.aspectSystemName}/${entry.periodId}`,
    rowsLabel: t('toolInspector.rowsLabel.period', { defaultValue: 'PERIOD' }),
    rows: [
      { label: t('toolInspector.rows.window', { defaultValue: 'Window' }), value: `${new Date(entry.window.from).toLocaleDateString()} – ${new Date(entry.window.to).toLocaleDateString()}` },
    ],
    acts: [
      { id: 'check_in_late', label: t('trajectory.acts.checkInLate', { defaultValue: 'Check in late' }), variant: 'secondary', icon: 'lucide:rotate-ccw' },
      { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
    ],
    note: t('trajectory.cadence.missNote', {
      defaultValue: 'Late check-in appends an observation with its true observedAt — the repair loop, not an edit.',
    }),
    context: { sphereId, aspectSystemName: track.aspectSystemName, periodId: entry.periodId, factorId: entry.factorId },
  };
}

/**
 * The "due" tick — submit in place; acts ARE the status choices (the three quick-tap statuses
 * `InspectorActId` defines: `set_status_on_track`/`at_risk`/`blocked`. Statuses beyond these three
 * (`needs_attention`, `critical`) are reachable via `CheckInInlineMolecule`'s full pill group, not
 * this one-tap tile.)
 */
export function buildCadenceDuePayload(
  track: TrajectoryTrack,
  entry: TrajectoryCadenceEntry,
  sphereId: string,
  t: TrajectoryTranslateFn
): DemandInspectorPayload {
  return {
    kind: 'demand',
    icon: 'lucide:clock',
    title: t('trajectory.cadence.dueTitle', { label: track.label, defaultValue: `${track.label} check-in due` }),
    subtitle: t('trajectory.cadence.dueSubtitle', { defaultValue: 'Submit in place — the highest-value interaction here.' }),
    address: `/sphere/${sphereId}/cadence/${track.aspectSystemName}/${entry.periodId}`,
    rowsLabel: t('toolInspector.rowsLabel.window', { defaultValue: 'WINDOW' }),
    rows: [
      { label: t('toolInspector.rows.closes', { defaultValue: 'Closes' }), value: entry.window.to, mono: true },
    ],
    acts: [
      { id: 'set_status_on_track', label: t('statusVocabulary.on_track'), variant: 'primary' },
      { id: 'set_status_at_risk', label: t('statusVocabulary.at_risk'), variant: 'secondary' },
      { id: 'set_status_blocked', label: t('statusVocabulary.blocked'), variant: 'secondary' },
    ],
    context: { sphereId, aspectSystemName: track.aspectSystemName, periodId: entry.periodId, factorId: entry.factorId },
  };
}

/** A named freshness deviant. */
export function buildStalenessPayload(
  deviant: TrajectoryFreshnessSummary['stale'][number],
  sphereId: string,
  t: TrajectoryTranslateFn
): StalenessInspectorPayload {
  return {
    kind: 'staleness',
    icon: 'lucide:alert-triangle',
    title: t('trajectory.staleness.title', { label: deviant.label, ageDays: deviant.ageDays, defaultValue: `${deviant.label} is ${deviant.ageDays}d stale` }),
    subtitle: t('trajectory.staleness.subtitle', {
      defaultValue: 'Past its revalidation window. Staleness is a property of the value, not a report about it.',
    }),
    address: `/sphere/${sphereId}/aspect/${deviant.aspectSystemName}`,
    rowsLabel: t('toolInspector.rowsLabel.window', { defaultValue: 'WINDOW' }),
    rows: [
      { label: t('toolInspector.rows.overdueBy', { defaultValue: 'Overdue by' }), value: `${deviant.ageDays}d` },
    ],
    acts: [
      { id: 'request_update', label: t('trajectory.acts.requestUpdate', { defaultValue: 'Request update' }), variant: 'primary', icon: 'lucide:bell' },
      { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
    ],
    note: t('trajectory.staleness.note', {
      defaultValue: 'Request update creates a demand for the Responsible — not a message.',
    }),
    context: { sphereId, aspectSystemName: deviant.aspectSystemName },
  };
}

/**
 * Who a demand is owed by, as the inspector names them: an unowned demand is the platform's own,
 * the viewer is addressed in the second person, and anyone else by name.
 */
function resolveDemandOwnerLabel(demand: TrajectoryDemand, t: TrajectoryTranslateFn): string {
  if (demand.owner.automatic) return t('trajectory.demand.automatic', { defaultValue: 'automatic' });
  if (demand.owner.isViewer) return t('trajectory.demand.you', { defaultValue: 'you' });
  return demand.owner.name ?? '';
}

/** A demand-stack row. */
export function buildFocusItemPayload(
  demand: TrajectoryDemand,
  sphereId: string,
  t: TrajectoryTranslateFn
): FocusItemInspectorPayload {
  const owner = resolveDemandOwnerLabel(demand, t);

  return {
    kind: 'focus_item',
    icon: 'lucide:clock',
    title: t('trajectory.demand.title', { aspect: demand.label, owner, defaultValue: `${demand.label} — owed by ${owner}` }),
    subtitle: t('trajectory.demand.subtitle', {
      defaultValue: demand.owner.isViewer
        ? 'Opens the Focus item. You fulfil inline.'
        : 'Opens the Focus item. Remind the owner.',
    }),
    address: `/focus/${demand.aspectSystemName.replaceAll('_', '-')}`,
    rowsLabel: t('toolInspector.rowsLabel.demand', { defaultValue: 'DEMAND' }),
    rows: [
      { label: t('toolInspector.rows.aspect', { defaultValue: 'Aspect' }), value: demand.label },
      { label: t('toolInspector.rows.owner', { defaultValue: 'Owner' }), value: owner },
      { label: t('toolInspector.rows.due', { defaultValue: 'Due' }), value: demand.dueAt, mono: true },
    ],
    acts: demand.owner.isViewer
      ? [
          { id: 'fulfil_inline', label: t('trajectory.acts.fulfilInline', { defaultValue: 'Fulfil inline' }), variant: 'primary', icon: 'lucide:check' },
          { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
        ]
      : [
          { id: 'remind_owner', label: t('trajectory.acts.remindOwner', { defaultValue: 'Remind owner' }), variant: 'secondary', icon: 'lucide:bell' },
          { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
        ],
    context: { sphereId, aspectSystemName: demand.aspectSystemName, periodId: demand.periodId, factorId: demand.factorId },
  };
}

/** A track title — POLICY. */
export function buildAspectPayload(
  track: TrajectoryTrack,
  sphereId: string,
  t: TrajectoryTranslateFn
): AspectInspectorPayload {
  const rows: InspectorKeyValueRow[] = [
    { label: t('toolInspector.rows.kind', { defaultValue: 'Kind' }), value: track.kind, mono: true },
  ];
  if (track.freshness.revalidateDays != null) {
    rows.push({ label: t('toolInspector.rows.revalidate', { defaultValue: 'Revalidate' }), value: t('trajectory.aspect.everyNDays', { count: track.freshness.revalidateDays, defaultValue: `every ${track.freshness.revalidateDays}d` }) });
  }

  return {
    kind: 'aspect',
    icon: 'lucide:layers',
    title: track.label,
    address: `/sphere/${sphereId}/aspect/${track.aspectSystemName}`,
    rowsLabel: t('toolInspector.rowsLabel.policy', { defaultValue: 'POLICY' }),
    rows,
    acts: [
      { id: 'open_full_track', label: t('trajectory.acts.openFullTrack', { defaultValue: 'Open full track' }), variant: 'secondary', icon: 'lucide:arrow-up-right' },
      { id: 'pin_to_glimpse', label: t('trajectory.acts.pinToGlimpse', { defaultValue: 'Pin to glimpse' }), variant: 'secondary', icon: 'lucide:pin' },
    ],
    context: { sphereId, aspectSystemName: track.aspectSystemName },
  };
}

/** The Freshness card title — per-aspect coverage list. */
export function buildCoveragePayload(
  freshness: TrajectoryFreshnessSummary,
  tracks: TrajectoryTrack[],
  sphereId: string,
  t: TrajectoryTranslateFn
): CoverageInspectorPayload {
  const staleNames = new Set(freshness.stale.map((s) => s.aspectSystemName));
  return {
    kind: 'coverage',
    icon: 'lucide:layers',
    title: t('trajectory.freshness.summary', { fresh: freshness.fresh, total: freshness.total, defaultValue: `Freshness — ${freshness.fresh} of ${freshness.total} aspects fresh` }),
    subtitle: t('trajectory.freshness.coverageSubtitle', {
      defaultValue: 'Coverage diagnostic from the health reducer. Only deviants are named.',
    }),
    address: `/sphere/${sphereId}/freshness`,
    rows: tracks
      .filter((track) => track.freshness.fresh !== null)
      .map((track) => ({
        label: track.label,
        value: staleNames.has(track.aspectSystemName)
          ? t('trajectory.freshness.staleAge', { ageDays: track.freshness.ageDays, defaultValue: `stale · ${track.freshness.ageDays}d` })
          : t('trajectory.freshness.freshAge', { ageDays: track.freshness.ageDays, defaultValue: `fresh · ${track.freshness.ageDays}d` }),
      })),
    acts: [{ id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' }],
    context: { sphereId },
  };
}
